import { AdbDaemonWebUsbDevice } from '@yume-chan/adb-daemon-webusb';
import { Adb as AdbDaemon } from '@yume-chan/adb';
import {
    ConcatStringStream,
    Consumable,
    ReadableStream,
    TextDecoderStream
} from '@yume-chan/stream-extra';
import { EventDispatcher } from '@/utility/events';
import { logger } from '@/api/logger';
import { isUsbTransferError } from '@/utility/usb';
import { AdbSession } from '@/api/adb/session';

export class Adb extends EventDispatcher {
    private readonly serial: string;
    declare private device: AdbDaemonWebUsbDevice;
    declare private adb: AdbDaemon;
    private session?: AdbSession;

    constructor(serial: string) {
        super();
        this.serial = serial;
    }

    public async connect() {
        // Connexion partagee : deux `Adb` sur le meme casque reutilisent le meme
        // transport au lieu de se disputer le handle USB. cf. `AdbSession`.
        this.session = await AdbSession.acquire(this.serial);
        this.device = this.session.device;
        this.adb = this.session.daemon;
    }

    public async close() {
        const session = this.session;
        if (!session) return;
        // Un seul `release()` par `connect()`, meme si l'appelant ferme deux fois.
        this.session = undefined;
        await session.release();
    }

    public async forget() {
        if (!this.device) return;
        try {
            logger.debug({ action: 'forget', serial: this.serial });
            return await this.device.raw.forget();
        } catch (e) {
            this.report('forget', e);
            throw e;
        }
    }

    public async ensureScreenOn(): Promise<void> {
        const res = await this.shell('dumpsys power');
        const isOn = res.match(/Display Power: state=(ON|OFF)/)![1] == 'ON';
        if (!isOn) await this.shell('input keyevent KEYCODE_WAKEUP');
    }

    public async shell(cmd: string) {
        try {
            let res: string;
            if (cmd.startsWith('date')) res = await this.execShell(cmd);
            else res = (await this.adb.subprocess.noneProtocol.spawnWaitText(cmd)).trim();
            if (res.indexOf('Error') == 0) throw res;
            logger.debug({
                action: 'shell',
                cmd: cmd,
                result: truncate(res),
                serial: this.serial
            });
            return res;
        } catch (e) {
            // Si c'est un reboot et une erreur USB, c'est normal (le device se déconnecte)
            if (cmd.includes('reboot') && isUsbTransferError(e)) {
                this.session?.fail(e);
                logger.debug({
                    action: 'shell',
                    cmd: cmd,
                    info: 'USB disconnected during reboot (expected)',
                    serial: this.serial
                });
                throw e; // On re-throw pour que le code appelant puisse la gérer
            }
            this.report('shell', e, { cmd: cmd });
            throw e;
        }
    }

    public async pull(path: string) {
        const sync = await this.adb.sync();
        try {
            const content = await sync
                .read(path)
                .pipeThrough(new TextDecoderStream())
                .pipeThrough(new ConcatStringStream());
            logger.debug({ action: 'pull', path: path, serial: this.serial });
            return content;
        } catch (e) {
            if (`${e}`.indexOf('No such file or directory') > -1) throw 'No such file or directory';
            this.report('pull', e, { path: path });
            throw e;
        } finally {
            await sync.dispose();
        }
    }

    /**
     * Taille reelle des fichiers sur le casque, en une seule session sync.
     * `null` pour un fichier absent. Aucun transfert : `lstat` ne lit que les
     * metadonnees, meme sur un fichier de plusieurs centaines de Mo.
     */
    public async fileSizes(paths: string[]) {
        const sizes = new Map<string, number | null>();
        if (paths.length == 0) return sizes;
        const sync = await this.adb.sync();
        try {
            for (const path of paths) {
                try {
                    sizes.set(path, Number((await sync.lstat(path)).size));
                } catch {
                    sizes.set(path, null);
                }
            }
            return sizes;
        } finally {
            await sync.dispose();
        }
    }

    public async push(file: File, path: string) {
        try {
            const totalBytes = file.size;
            const reader = file.stream().getReader();
            logger.debug({ action: 'push', path: path, serial: this.serial });
            return await this.transmit(reader, path, totalBytes);
        } catch (e) {
            this.report('push', e, { path: path });
            throw e;
        }
    }

    public async stream(url: string, path: string, fileName = '') {
        if (fileName == '') fileName = url.split('/').pop()!;
        const target = `${path}/${fileName}`;
        try {
            const resp = await fetch(url + '?' + Math.random());
            const totalBytes = Number.parseInt(resp.headers.get('Content-Length')!);
            const reader = resp.body!.getReader();
            logger.debug({
                action: 'stream',
                fileName: fileName,
                path: path,
                size: totalBytes,
                serial: this.serial
            });
            const res = await this.transmit(reader, target, totalBytes);
            logger.debug({
                action: 'streamed',
                fileName: fileName,
                path: path,
                size: totalBytes,
                serial: this.serial
            });
            return res;
        } catch (e) {
            this.report('stream', e, { fileName: fileName, path: path });
            throw e;
        }
    }

    private async execShell(cmd: string) {
        // `shellProtocol` sépare réellement stdout de stderr, mais n'est disponible
        // que si le device annonce la feature `shell_v2`.
        const shell = this.adb.subprocess.shellProtocol;
        if (!shell) return (await this.adb.subprocess.noneProtocol.spawnWaitText(cmd)).trim();
        const process = await shell.spawn(cmd);
        return (
            await process.stdout
                .pipeThrough(new TextDecoderStream())
                .pipeThrough(new ConcatStringStream())
        ).trim();
    }

    private async transmit(
        reader: ReadableStreamDefaultReader<Uint8Array>,
        path: string,
        totalBytes: number
    ) {
        const serial = this.serial;
        let sentBytes = 0;
        const transmit = async (
            controller: ReadableStreamDefaultController<Consumable<Uint8Array>>
        ): Promise<boolean> => {
            const res = await reader.read();
            if (res.done) return false;
            sentBytes += res.value!.length;
            controller.enqueue(new Consumable(res.value));
            this.dispatchEvent(
                new ProgressEvent('progress', { loaded: sentBytes, total: totalBytes })
            );
            return true;
        };
        const fileInput = new ReadableStream<Consumable<Uint8Array>>({
            async start(controller) {
                try {
                    while (await transmit(controller));
                    controller.close();
                    reader.releaseLock();
                } catch (e) {
                    // Sans trace ni `controller.error()`, un transfert interrompu ici
                    // passait pour une fin de fichier normale cote ADB : `sync.write`
                    // restait en attente ou rendait la main comme si tout allait bien.
                    logger.error({
                        action: 'transmit',
                        path: path,
                        sent: sentBytes,
                        total: totalBytes,
                        serial: serial,
                        error: `${e}`
                    });
                    try {
                        controller.error(e);
                    } catch {
                        // flux deja ferme ou annule : rien de plus a signaler
                    }
                }
            },
            cancel(reason?: unknown) {
                logger.error({
                    action: 'transmit cancel',
                    path: path,
                    sent: sentBytes,
                    total: totalBytes,
                    serial: serial,
                    error: `${reason}`
                });
            }
        });
        const sync = await this.adb.sync();
        await sync.write({ filename: path, file: fileInput, permission: 0o644 });
        await sync.dispose();
        return path;
    }

    /**
     * Le transport ADB ne survit pas a une coupure USB : on invalide la session
     * partagee pour que la prochaine connexion reparte sur un transport neuf.
     */
    private report(action: string, error: unknown, context: object = {}) {
        if (isUsbTransferError(error)) this.session?.fail(error);
        logger.error({ action: action, serial: this.serial, ...context, error: `${error}` });
    }

    wait(milliseconds: number) {
        return new Promise((resolve) => setTimeout(resolve, milliseconds));
    }

    public async reboot() {
        try {
            await this.adb.power.reboot();
        } catch (e) {
            if (!isUsbTransferError(e)) {
                this.report('reboot', e);
                throw e;
            }
            logger.debug({
                action: 'reboot',
                serial: this.serial,
                info: 'USB disconnected during reboot (expected)'
            });
        }
        // Le casque redemarre : la session partagee ne vaut plus rien.
        this.session?.fail('reboot');
        try {
            await this.close();
        } catch (e) {
            if (!isUsbTransferError(e)) {
                logger.error({ action: 'reboot close', serial: this.serial, error: `${e}` });
            }
        }
    }
}

/** Les resultats etaient coupes a 50 caracteres, souvent avant l'info utile. */
function truncate(res: string, max = 240): string {
    if (res.length <= max) return res;
    return `${res.substring(0, max)}… (+${res.length - max})`;
}
